import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { uploadPhoto } from "../lib/upload";
import type {
  CreateNewsRequest,
  NewsItem,
  UpdateNewsRequest,
} from "@family/shared";

interface NewsFormProps {
  familySlug: string;
  item?: NewsItem;
  onSaved?: (saved: NewsItem) => void;
  onCancel?: () => void;
}

export function NewsForm({
  familySlug,
  item,
  onSaved,
  onCancel,
}: NewsFormProps) {
  const isEdit = Boolean(item);
  const coverInputRef = useRef<HTMLInputElement | null>(null);

  const [title, setTitle] = useState(item?.title ?? "");
  const [summary, setSummary] = useState(item?.summary ?? "");
  const [body, setBody] = useState(item?.body ?? "");
  const [category, setCategory] = useState(item?.category ?? "family_news");
  const [publishedAt, setPublishedAt] = useState<string>(() => {
    if (item) return item.publishedAt.slice(0, 10);
    return new Date().toISOString().slice(0, 10);
  });
  const [coverMediaId, setCoverMediaId] = useState<string | null>(
    item?.coverMediaId ?? null,
  );
  const [coverPreview, setCoverPreview] = useState<string | null>(
    item?.coverUrl ?? null,
  );
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [coverError, setCoverError] = useState<string | null>(null);

  const save = useMutation<
    NewsItem,
    Error,
    CreateNewsRequest | UpdateNewsRequest
  >({
    mutationFn: (payload) =>
      isEdit
        ? apiFetch<NewsItem>(
            `/families/${familySlug}/news/${item!.id}`,
            { method: "PATCH", body: JSON.stringify(payload) },
          )
        : apiFetch<NewsItem>(`/families/${familySlug}/news`, {
            method: "POST",
            body: JSON.stringify(payload),
          }),
    onSuccess: onSaved,
  });

  async function onPickCover(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setCoverPreview(localUrl);
    setCoverUploading(true);
    setCoverProgress(0);
    setCoverError(null);
    try {
      const mediaId = await uploadPhoto(familySlug, file, {
        onProgress: setCoverProgress,
      });
      setCoverMediaId(mediaId);
    } catch (err) {
      setCoverError(err instanceof Error ? err.message : String(err));
      setCoverPreview(item?.coverUrl ?? null);
    } finally {
      setCoverUploading(false);
    }
  }

  function removeCover() {
    setCoverMediaId(null);
    setCoverPreview(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    if (coverUploading) return;
    const payload: CreateNewsRequest = {
      title: title.trim(),
      summary: summary.trim() || undefined,
      body: body.trim() || undefined,
      category: category.trim() || undefined,
      coverMediaId,
      publishedAt: publishedAt
        ? new Date(publishedAt + "T12:00:00Z").toISOString()
        : undefined,
    };
    save.mutate(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="card stack">
      <h3 style={{ fontSize: "1rem", margin: 0 }}>
        {isEdit ? "Edit news" : "New news item"}
      </h3>

      <div>
        <label>Cover image</label>
        <div
          style={{
            display: "flex",
            gap: "var(--s-3)",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: 160,
              height: 120,
              flexShrink: 0,
              borderRadius: 4,
              overflow: "hidden",
              background: "var(--c-bg-alt)",
              border: "1px solid var(--c-border)",
              position: "relative",
            }}
          >
            {coverPreview ? (
              <img
                src={coverPreview}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  opacity: coverUploading ? 0.5 : 1,
                }}
                draggable={false}
                onContextMenu={(ev) => ev.preventDefault()}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--c-text-subtle)",
                  fontSize: "0.85rem",
                }}
              >
                No cover
              </div>
            )}
            {coverUploading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 700,
                  textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                }}
              >
                {coverProgress}%
              </div>
            )}
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "var(--s-2)",
            }}
          >
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={onPickCover}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => coverInputRef.current?.click()}
              disabled={coverUploading || save.isPending}
            >
              {coverUploading
                ? "Uploading…"
                : coverPreview
                  ? "Replace image"
                  : "Upload image"}
            </button>
            {coverPreview && !coverUploading && (
              <button
                type="button"
                className="btn-tertiary"
                onClick={removeCover}
                disabled={save.isPending}
                style={{ alignSelf: "flex-start", color: "var(--c-danger)" }}
              >
                Remove image
              </button>
            )}
            {coverError && (
              <p className="text-error" style={{ fontSize: "0.85rem" }}>
                {coverError}
              </p>
            )}
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="n-title">Title *</label>
        <input
          id="n-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          disabled={save.isPending}
        />
      </div>

      <div>
        <label htmlFor="n-summary">Summary</label>
        <input
          id="n-summary"
          type="text"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="One-line teaser shown on the list page"
          disabled={save.isPending}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "var(--s-3)",
        }}
      >
        <div>
          <label htmlFor="n-category">Category</label>
          <input
            id="n-category"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="family_news"
            disabled={save.isPending}
          />
        </div>
        <div>
          <label htmlFor="n-published">Published</label>
          <input
            id="n-published"
            type="date"
            value={publishedAt}
            onChange={(e) => setPublishedAt(e.target.value)}
            disabled={save.isPending}
          />
        </div>
      </div>

      <div>
        <label htmlFor="n-body">Body</label>
        <textarea
          id="n-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          disabled={save.isPending}
          style={{
            width: "100%",
            background: "var(--c-bg)",
            color: "var(--c-text)",
            border: "1px solid var(--c-border-strong)",
            borderRadius: 4,
            padding: "14px 16px",
            fontSize: "1rem",
            fontFamily: "inherit",
            resize: "vertical",
          }}
        />
      </div>

      {save.error && <p className="text-error">{save.error.message}</p>}

      <div style={{ display: "flex", gap: "var(--s-3)" }}>
        <button
          type="submit"
          disabled={save.isPending || !title.trim() || coverUploading}
        >
          {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Publish"}
        </button>
        {onCancel && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={save.isPending}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
